import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { FileApiService, FILE_BASE_URL } from '@angular-workspace/services';
import {
  encryptAndUploadChunks,
  encryptedPartCount,
  manifestKeyFor,
  EncryptedDatasetManifest,
} from '../../../lib/encrypted-dataset-upload';

export interface EncryptionPublicKey {
  publicKeyPem: string;
  kmsKeyVersion: string;
  algorithm: string;
}

/**
 * Client-side-encrypted dataset upload through the existing files-connect-api
 * presigned multipart flow:
 *
 *   1. GET  /v1/encryption/public-key            (KMS public key for DEK wrapping)
 *   2. POST /v1/databanks/{id}/uploads           (numParts = ceil(size / 16 MB))
 *   3. PUT  each AES-256-GCM-encrypted chunk to its presigned part URL
 *   4. PUT  /v1/databanks/{id}/uploads/{uploadId} (server composes parts in order)
 *   5. Upload `${key}.manifest.json` sidecar (single-part, same flow) — written
 *      only after complete succeeds, so a missing manifest means "not finalized"
 *
 * The composed object is ciphertext; only the TEE (via KMS asymmetricDecrypt on
 * the manifest's wrapped DEK) can decrypt it. No preview is possible.
 */
@Injectable({ providedIn: 'root' })
export class EncryptedUploadService {
  private publicKeyCache: { value: EncryptionPublicKey; fetchedAt: number } | null = null;

  constructor(
    private fileApiService: FileApiService,
    private http: HttpClient,
    @Inject(FILE_BASE_URL) private fileBaseUrl: string,
  ) {}

  /** Fetch (and briefly cache) the platform's DEK-wrapping public key. */
  async getEncryptionPublicKey(): Promise<EncryptionPublicKey> {
    const TTL_MS = 5 * 60 * 1000;
    if (this.publicKeyCache && Date.now() - this.publicKeyCache.fetchedAt < TTL_MS) {
      return this.publicKeyCache.value;
    }
    const url = `${this.fileBaseUrl}v1/encryption/public-key`;
    const resp: any = await firstValueFrom(this.http.get(url));
    const value = (resp?.data ?? resp) as EncryptionPublicKey;
    if (!value?.publicKeyPem) {
      throw new Error('Encryption public key unavailable — is KMS configured on the fileserver?');
    }
    this.publicKeyCache = { value, fetchedAt: Date.now() };
    return value;
  }

  /**
   * Encrypt and upload one file under `key`, then upload its manifest sidecar.
   * Returns the manifest. `onProgress` reports 0–100 across chunk uploads.
   */
  async uploadEncryptedFile(
    databankId: string,
    key: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<EncryptedDatasetManifest> {
    const publicKey = await this.getEncryptionPublicKey();
    const numParts = encryptedPartCount(file.size);

    const initiateResponse = await this.fileApiService.initiateUpload(
      databankId,
      key,
      'application/octet-stream',
      numParts,
    );
    const responseData = initiateResponse.data.data || initiateResponse.data;
    const uploadId: string = responseData.uploadId;
    const parts: Array<{ partNumber: number; presignedUrl: string }> = responseData.parts || [];
    if (!uploadId || parts.length < numParts) {
      throw new Error(`Upload initiation returned ${parts.length}/${numParts} part URLs`);
    }

    const completedParts: Array<{ partNumber: number; eTag: string }> = [];
    try {
      const manifest = await encryptAndUploadChunks({
        file,
        publicKeyPem: publicKey.publicKeyPem,
        kmsKeyVersion: publicKey.kmsKeyVersion,
        putPart: async (chunkIndex, bytes) => {
          const part = parts[chunkIndex];
          const uploadResponse = await this.fileApiService.uploadPart(
            part.presignedUrl,
            new Blob([bytes as BlobPart], { type: 'application/octet-stream' }),
            { headers: { 'Content-Type': 'application/octet-stream' } },
          );
          const eTag = uploadResponse.headers.get('ETag')?.replace(/"/g, '') || '';
          if (!eTag) {
            throw new Error(`No ETag received for part ${part.partNumber}`);
          }
          completedParts[chunkIndex] = { partNumber: part.partNumber, eTag };
        },
        ...(onProgress ? { onProgress } : {}),
      });

      await this.fileApiService.completeUpload(databankId, uploadId, key, completedParts);
      await this.uploadManifest(databankId, key, manifest);
      return manifest;
    } catch (err) {
      // Best-effort cleanup of staged parts; the original error is what matters.
      try {
        await this.fileApiService.cancelUpload(databankId, uploadId, key);
      } catch { /* staging objects are also cleaned by any later re-upload */ }
      throw err;
    }
  }

  /** Upload the manifest sidecar as an ordinary single-part object. */
  private async uploadManifest(
    databankId: string,
    objectKey: string,
    manifest: EncryptedDatasetManifest,
  ): Promise<void> {
    const key = manifestKeyFor(objectKey);
    const body = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });

    const initiateResponse = await this.fileApiService.initiateUpload(
      databankId,
      key,
      'application/json',
      1,
    );
    const responseData = initiateResponse.data.data || initiateResponse.data;
    const uploadId: string = responseData.uploadId;
    const presignedUrl: string = responseData.parts?.[0]?.presignedUrl;
    if (!uploadId || !presignedUrl) {
      throw new Error('Manifest upload initiation returned no presigned URL');
    }

    const uploadResponse = await this.fileApiService.uploadPart(presignedUrl, body, {
      headers: { 'Content-Type': 'application/json' },
    });
    const eTag = uploadResponse.headers.get('ETag')?.replace(/"/g, '') || '';
    if (!eTag) {
      throw new Error('No ETag received for manifest upload');
    }

    await this.fileApiService.completeUpload(databankId, uploadId, key, [
      { partNumber: 1, eTag },
    ]);
  }
}
